// AetherAudio.tsx — Procedural Instrumental Ambient Soundscape
//
// Architecture: Uses Web Audio API directly (AudioContext + oscillators + filters)
// NOT Three.js PositionalAudio (since the ambient should be omnidirectional)
//
// Sound design: Dark, ethereal drone with:
// - Base layer: Very low sine wave (55Hz, A1) at low gain
// - Pad layer: Detuned triangle waves (110Hz ± 2Hz) for shimmer
// - Sub layer: Ultra-low sine (27.5Hz) for depth
// - Filter: Low-pass at 800Hz, gentle sweep
// - Master gain: 0.35 (background-focused)
//
// Thinking modulation:
// - When isThinking=true: increase filter cutoff to 1200Hz, add subtle gain boost (+0.05)
// - When isThinking=false: ease back to baseline
// - All transitions use linearRampToValueAtTime for smooth fades
//
// IMPORTANT: AudioContext can only be created/resumed after user gesture.
// Initialize AudioContext lazily on first enable.

import React, { useEffect, useRef, useCallback } from 'react';

interface AetherAudioNodes {
  ctx: AudioContext;
  masterGain: GainNode;
  filter: BiquadFilterNode;
  baseOsc: OscillatorNode;
  baseGain: GainNode;
  padOsc1: OscillatorNode;
  padOsc2: OscillatorNode;
  padGain: GainNode;
  subOsc: OscillatorNode;
  subGain: GainNode;
  lfo: OscillatorNode;
  lfoGain: GainNode;
}

const MASTER_GAIN = 0.35;
const FILTER_BASE = 800;
const FILTER_THINKING = 1200;
const THINKING_GAIN_BOOST = 0.05;
const RAMP_TIME = 1.5; // seconds for smooth transitions

const AetherAudio: React.FC<{ enabled: boolean; isThinking: boolean }> = ({ enabled, isThinking }) => {
  const nodesRef = useRef<AetherAudioNodes | null>(null);
  const initializedRef = useRef(false);

  const createAudioGraph = useCallback((): AetherAudioNodes => {
    const ctx = new AudioContext();

    // Master gain — controls overall volume
    const masterGain = ctx.createGain();
    masterGain.gain.value = 0; // Start silent, will ramp up
    masterGain.connect(ctx.destination);

    // Low-pass filter for warmth
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = FILTER_BASE;
    filter.Q.value = 1.5;
    filter.connect(masterGain);

    // Base layer: Sine wave at 55Hz (A1) — foundational drone
    const baseOsc = ctx.createOscillator();
    baseOsc.type = 'sine';
    baseOsc.frequency.value = 55;
    const baseGain = ctx.createGain();
    baseGain.gain.value = 0.4;
    baseOsc.connect(baseGain);
    baseGain.connect(filter);

    // Pad layer: Detuned triangle waves for shimmer
    const padOsc1 = ctx.createOscillator();
    padOsc1.type = 'triangle';
    padOsc1.frequency.value = 108; // 110Hz - 2Hz detune
    const padOsc2 = ctx.createOscillator();
    padOsc2.type = 'triangle';
    padOsc2.frequency.value = 112; // 110Hz + 2Hz detune
    const padGain = ctx.createGain();
    padGain.gain.value = 0.15;
    padOsc1.connect(padGain);
    padOsc2.connect(padGain);
    padGain.connect(filter);

    // Sub layer: Ultra-low sine (27.5Hz, A0) for depth
    const subOsc = ctx.createOscillator();
    subOsc.type = 'sine';
    subOsc.frequency.value = 27.5;
    const subGain = ctx.createGain();
    subGain.gain.value = 0.3;
    subOsc.connect(subGain);
    subGain.connect(filter);

    // LFO: Slow modulation of the filter cutoff for movement
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.05; // Very slow sweep (20s cycle)
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 200; // ±200Hz modulation range
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);

    // Start all oscillators
    baseOsc.start();
    padOsc1.start();
    padOsc2.start();
    subOsc.start();
    lfo.start();

    return {
      ctx,
      masterGain,
      filter,
      baseOsc,
      baseGain,
      padOsc1,
      padOsc2,
      padGain,
      subOsc,
      subGain,
      lfo,
      lfoGain,
    };
  }, []);

  // Handle enable/disable — create or resume/suspend AudioContext
  useEffect(() => {
    if (enabled) {
      if (!nodesRef.current) {
        // First time: create the entire audio graph (requires user gesture already happened)
        const nodes = createAudioGraph();
        nodesRef.current = nodes;
        initializedRef.current = true;

        // Fade in master gain smoothly
        const now = nodes.ctx.currentTime;
        nodes.masterGain.gain.setValueAtTime(0, now);
        nodes.masterGain.gain.linearRampToValueAtTime(MASTER_GAIN, now + RAMP_TIME);
      } else {
        // Resume suspended context
        const nodes = nodesRef.current;
        if (nodes.ctx.state === 'suspended') {
          nodes.ctx.resume().then(() => {
            const now = nodes.ctx.currentTime;
            nodes.masterGain.gain.setValueAtTime(nodes.masterGain.gain.value, now);
            nodes.masterGain.gain.linearRampToValueAtTime(MASTER_GAIN, now + RAMP_TIME);
          });
        }
      }
    } else {
      // Disabled: fade out then suspend
      if (nodesRef.current && nodesRef.current.ctx.state === 'running') {
        const nodes = nodesRef.current;
        const now = nodes.ctx.currentTime;
        nodes.masterGain.gain.setValueAtTime(nodes.masterGain.gain.value, now);
        nodes.masterGain.gain.linearRampToValueAtTime(0, now + RAMP_TIME);

        // Suspend after fade-out completes
        setTimeout(() => {
          if (nodesRef.current && nodesRef.current.ctx.state === 'running') {
            nodesRef.current.ctx.suspend();
          }
        }, RAMP_TIME * 1000 + 100);
      }
    }
  }, [enabled, createAudioGraph]);

  // Handle thinking modulation: filter cutoff + subtle gain boost
  useEffect(() => {
    if (!nodesRef.current || !enabled) return;

    const nodes = nodesRef.current;
    if (nodes.ctx.state !== 'running') return;

    const now = nodes.ctx.currentTime;

    if (isThinking) {
      // Increase filter cutoff for brighter sound during thinking
      nodes.filter.frequency.setValueAtTime(nodes.filter.frequency.value, now);
      nodes.filter.frequency.linearRampToValueAtTime(FILTER_THINKING, now + RAMP_TIME * 0.5);

      // Subtle gain boost
      nodes.masterGain.gain.setValueAtTime(nodes.masterGain.gain.value, now);
      nodes.masterGain.gain.linearRampToValueAtTime(MASTER_GAIN + THINKING_GAIN_BOOST, now + RAMP_TIME * 0.5);
    } else {
      // Ease back to baseline
      nodes.filter.frequency.setValueAtTime(nodes.filter.frequency.value, now);
      nodes.filter.frequency.linearRampToValueAtTime(FILTER_BASE, now + RAMP_TIME);

      nodes.masterGain.gain.setValueAtTime(nodes.masterGain.gain.value, now);
      nodes.masterGain.gain.linearRampToValueAtTime(MASTER_GAIN, now + RAMP_TIME);
    }
  }, [isThinking, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (nodesRef.current) {
        const nodes = nodesRef.current;
        try {
          nodes.baseOsc.stop();
          nodes.padOsc1.stop();
          nodes.padOsc2.stop();
          nodes.subOsc.stop();
          nodes.lfo.stop();
          nodes.baseOsc.disconnect();
          nodes.padOsc1.disconnect();
          nodes.padOsc2.disconnect();
          nodes.subOsc.disconnect();
          nodes.lfo.disconnect();
          nodes.baseGain.disconnect();
          nodes.padGain.disconnect();
          nodes.subGain.disconnect();
          nodes.lfoGain.disconnect();
          nodes.filter.disconnect();
          nodes.masterGain.disconnect();
          nodes.ctx.close();
        } catch {
          // Ignore errors during cleanup
        }
        nodesRef.current = null;
      }
    };
  }, []);

  // No DOM output
  return null;
};

export default AetherAudio;
